import { useCallback, useEffect, useState } from 'react'
import { api } from './api'
import type { areaBody } from './area'
import type { YieldUnit } from './yield'
import type { Field, IrrigationMethod, ProductionType, WaterSource } from './types'

/**
 * What the field form sends. Everything is optional on an edit; a create needs the name and area.
 * `variety` and `yearPlanted` are planting details: the server applies them to the field's single planting.
 */
export type FieldBody = {
  name?: string
  areaM2?: ReturnType<typeof areaBody>
  notes?: string | null
  variety?: string | null
  yearPlanted?: number | null
  irrigationMethods?: IrrigationMethod[]
  waterSources?: WaterSource[]
  productionType?: ProductionType | null
  previousCrop?: string | null
  expectedYieldKgPerHa?: { value: number; unit: YieldUnit } | null
}
export type NewFieldBody = FieldBody & Required<Pick<FieldBody, 'name' | 'areaM2'>>

export function useFields(orgId: string | undefined, farmId: string | undefined) {
  const [fields, setFields] = useState<Field[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId || !farmId) {
      setFields([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      setFields(await api.get<Field[]>(`/orgs/${orgId}/farms/${farmId}/fields?limit=200`))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load fields.')
    } finally {
      setLoading(false)
    }
  }, [orgId, farmId])

  useEffect(() => {
    refresh()
  }, [refresh])

  async function addField(body: NewFieldBody) {
    if (!orgId || !farmId) return
    const created = await api.post<Field>(`/orgs/${orgId}/farms/${farmId}/fields`, body)
    setFields((prev) => [created, ...prev])
  }

  return { fields, loading, error, addField }
}
